import React, { useMemo } from "react";

import { IMessageClaimDetail, IMessageMetadata } from "../../../../types/chat";
import { ChatHelper } from "../../../helpers/chatHelper";
import ChatMarkdownDataTable, {
  MarkdownDataTableColumn,
} from "./ChatMarkdownDataTable";
import ChatMessageExpandableTable from "./ChatMessageExpandableTable";
import ChatMessageUnavailable from "./ChatMessageUnavailable";
import MarkdownRenderer from "../../../ui/MarkdownRenderer";
import CoreExpander from "../../../ui/core/CoreExpander";
import { Typography } from "@mui/material";

const LINE_ANALYSIS_COLUMNS: MarkdownDataTableColumn[] = [
  { value: "line_number", label: "Line" },
  { value: "cpt_hcpcs_code", label: "Code" },
  { value: "cpt_description", label: "Description" },
  { value: "billed_amount", label: "Billed" },
  { value: "allowed_amount", label: "Allowed" },
  { value: "paid_amount", label: "Paid" },
  { value: "carc_codes", label: "CARC" },
  { value: "rarc_codes", label: "RARC" },
  { value: "line_classification", label: "Classification" },
  { value: "denial_category", label: "Denial" },
  { value: "contracted_rate", label: "Contracted" },
  { value: "payment_validation_result", label: "Validation" },
  { value: "coding_finding_ids", label: "Findings" },
  { value: "valid_denial", label: "Valid?" },
  { value: "valid_denial_reason", label: "Reason" },
  { value: "recovery_opportunity", label: "Recovery" },
];

const CODING_FINDINGS_COLUMNS: MarkdownDataTableColumn[] = [
  { value: "finding_id", label: "#" },
  { value: "check_category", label: "Category" },
  { value: "severity", label: "Severity" },
  { value: "affected_lines", label: "Affected lines" },
  { value: "description", label: "Description" },
  { value: "reference_source", label: "Reference" },
];

const OVERTURN_SCORES_COLUMNS: MarkdownDataTableColumn[] = [
  { value: "line_number", label: "Line" },
  { value: "probability", label: "Probability" },
  { value: "percentage_range", label: "Percentage range" },
  { value: "rationale", label: "Rationale" },
];

const CLAIM_OVERVIEW_COLUMNS = [
  { value: "field", label: "Field" },
  { value: "value", label: "Value" },
];

const WORK_QUEUE_ENTRY_COLUMNS = [
  { value: "field", label: "Field" },
  { value: "value", label: "Value" },
];

const PAYMENT_ANALYSIS_COLUMNS = [
  { value: "line_number", label: "Line" },
  { value: "cpt_hcpcs_code", label: "Code" },
  { value: "cpt_description", label: "Description" },
  { value: "billed_amount", label: "Billed" },
  { value: "allowed_amount", label: "Allowed" },
  { value: "paid_amount", label: "Paid" },
  { value: "payment_validation_result", label: "Validation" },
  { value: "carc_codes", label: "CARC" },
  { value: "rarc_codes", label: "RARC" },
  { value: "line_classification", label: "Classification" },
  { value: "coding_issues", label: "Issues" },
  { value: "paid_correctly", label: "Paid correctly" },
  { value: "payment_note", label: "Note" },
];

const COLUMNS_MAP = {
  overview: CLAIM_OVERVIEW_COLUMNS,
  line_analyses: LINE_ANALYSIS_COLUMNS,
  coding_findings: CODING_FINDINGS_COLUMNS,
  work_queue_entry: WORK_QUEUE_ENTRY_COLUMNS,
  payment_analysis: PAYMENT_ANALYSIS_COLUMNS,
  overturn_scores: OVERTURN_SCORES_COLUMNS,
};

const DEFAULT_EXPANDED_CLAIM_DETAILS = ["overview", "executive_summary"];

interface ChatMessageClaimDetailProps {
  data: IMessageClaimDetail | string;
  metadata?: IMessageMetadata;
}

const parseClaimDetail = (
  data: IMessageClaimDetail | string,
): IMessageClaimDetail | null => {
  if (typeof data !== "string") {
    return data;
  }

  try {
    return JSON.parse(data) as IMessageClaimDetail;
  } catch {
    return null;
  }
};

/** Ex: total_patient_responsibility -> Total Patient Responsibility */
const formatFieldKey = (key: string): string => {
  if (!key) return "";

  return key
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const objectToColumns = (object: Record<string, unknown>) => {
  return Object.entries(object).map(([key, value]) => ({
    field: formatFieldKey(key),
    value,
  }));
};

// REV-2299: column set for an array section with no COLUMNS_MAP entry (a new
// or renamed backend field), derived from whatever keys the rows actually carry.
const deriveColumnsFromObjects = (
  items: Record<string, unknown>[],
): MarkdownDataTableColumn[] => {
  const keys: string[] = [];

  items.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (!keys.includes(key)) keys.push(key);
    });
  });

  return keys.map((key) => ({ value: key, label: formatFieldKey(key) }));
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const OBJECT_TYPE_PROPS = ["overview", "work_queue_entry"];
const MARKDOWN_TYPE_PROPS = [
  "issue_summary",
  "executive_summary",
  "resolutions",
];

const ROW_KEY_FIELD_MAP = {
  overview: "field",
  line_analyses: "line_number",
  coding_findings: "coding_findings",
  work_queue_entry: "work_queue_entry",
};

const SUPPORTED_SECTIONS = [
  "overview",
  "line_analyses",
  "coding_findings",
  "payment_analysis",
  "issue_summary",
  "resolutions",
  "work_queue_entry",
  "overturn_scores",
  "executive_summary",
];

/** True when the object carries at least one claim-review section.
 * A fenced JSON reply with a different shape (for example a QC verdict)
 * must not be drawn as this card — every section would be skipped and the
 * bubble would be empty. */
function hasClaimReviewSection(parsed: IMessageClaimDetail): boolean {
  const record = parsed as Record<string, unknown>;
  return SUPPORTED_SECTIONS.some((section) => {
    const value = record[section];
    return value != null && value !== "";
  });
}

const ChatMessageClaimDetail: React.FC<ChatMessageClaimDetailProps> = ({
  data,
  metadata,
}) => {
  const parsed = useMemo(() => parseClaimDetail(data), [data]);

  const order =
    parsed?.section_display_order && parsed?.section_display_order.length > 0
      ? parsed?.section_display_order
      : SUPPORTED_SECTIONS;

  if (!parsed || !hasClaimReviewSection(parsed)) {
    // Don't render an empty bubble when the JSON fails to parse, or when it
    // parsed but has none of the claim-review sections this card knows how to draw.
    return <ChatMessageUnavailable raw={data} />;
  }

  return (
    <>
      {parsed?.title ? (
        <Typography
          sx={{ marginBottom: "25px", fontSize: "18px", fontWeight: "bold" }}
        >
         {parsed.title}
        </Typography>
      ) : null}

      {order.map((section, index) => {
        // REV-2299: a section absent from SUPPORTED_SECTIONS (e.g. a new or
        // renamed backend field not yet added here) still renders through the
        // generic fallback at the end of this loop instead of vanishing.
        const value = (parsed as Record<string, unknown>)?.[section];
        if (!value) {
          return null;
        }

        if (MARKDOWN_TYPE_PROPS?.includes(section)) {
          return (
            <CoreExpander
              key={`${section}-${index}`}
              title={parsed?.section_labels?.[section] ?? section}
              defaultExpanded={
                index === 0 || DEFAULT_EXPANDED_CLAIM_DETAILS.includes(section)
              }
            >
              <MarkdownRenderer
                content={String(value) as string}
                metadata={metadata}
              />
            </CoreExpander>
          );
        }

        let sectionData: Record<string, unknown>[];
        let columns = COLUMNS_MAP[section as keyof typeof COLUMNS_MAP];
        let rowKeyField =
          ROW_KEY_FIELD_MAP[section as keyof typeof ROW_KEY_FIELD_MAP];

        if (OBJECT_TYPE_PROPS.includes(section)) {
          const objectValue = (value as Record<string, unknown>) ?? {};

          if (
            isPlainObject(objectValue) &&
            ChatHelper.isColumnsFieldsObject(objectValue)
          ) {
            columns = [
              { value: "label", label: objectValue.columns[0] },
              { value: "value", label: objectValue.columns[1] },
            ];
            rowKeyField = "label";
            sectionData = objectValue.fields;
          } else {
            sectionData = objectToColumns(objectValue);
          }
        } else {
          sectionData = (value ?? []) as Record<string, unknown>[];
        }

        if (
          sectionData &&
          Array.isArray(sectionData) &&
          sectionData?.length > 0
        ) {
          const title = parsed?.section_labels?.[section] ?? section;
          const defaultExpanded = index === 0;

          // REV-2299: a section with no COLUMNS_MAP entry (unknown/new field)
          // still gets a usable table instead of undefined columns.
          if (!columns && isPlainObject(sectionData[0])) {
            columns = deriveColumnsFromObjects(sectionData);
          }

          // overview/work_queue_entry are single-entity field/value tables, not
          // a multi-row listing, so they don't get the "Expand Table" action.
          if (OBJECT_TYPE_PROPS.includes(section)) {
            return (
              <CoreExpander
                key={`${section}-${index}`}
                title={title}
                defaultExpanded={defaultExpanded}
              >
                <ChatMarkdownDataTable
                  columns={columns}
                  data={sectionData}
                  rowKeyField={rowKeyField}
                  metadata={metadata}
                />
              </CoreExpander>
            );
          }

          if (columns) {
            return (
              <ChatMessageExpandableTable
                key={`${section}-${index}`}
                title={title}
                defaultExpanded={defaultExpanded}
                columns={columns}
                data={sectionData}
                rowKeyField={rowKeyField}
                metadata={metadata}
              />
            );
          }

          // sectionData is an array of primitives (e.g. an unrecognized section
          // holding a plain string/number list) -> render as a bullet list.
          const listMarkdown = sectionData
            .map((item) => `- ${item}`)
            .join("\n");
          return (
            <CoreExpander
              key={`${section}-${index}`}
              title={title}
              defaultExpanded={defaultExpanded}
            >
              <MarkdownRenderer content={listMarkdown} metadata={metadata} />
            </CoreExpander>
          );
        }

        // An empty array (e.g. no line-level findings) genuinely has nothing
        // to show — distinct from an unrecognized non-array value below.
        if (Array.isArray(value)) {
          return null;
        }

        // REV-2299: value didn't match any known section shape above (unknown
        // scalar, or a plain object outside OBJECT_TYPE_PROPS) — show it rather
        // than drop it, so a new/renamed backend field never vanishes silently.
        const title = parsed?.section_labels?.[section] ?? section;
        if (isPlainObject(value)) {
          const rows = objectToColumns(value);
          if (!rows.length) return null;
          return (
            <CoreExpander
              key={`${section}-${index}`}
              title={title}
              defaultExpanded={index === 0}
            >
              <ChatMarkdownDataTable
                columns={CLAIM_OVERVIEW_COLUMNS}
                data={rows}
                rowKeyField="field"
                metadata={metadata}
              />
            </CoreExpander>
          );
        }

        return (
          <CoreExpander
            key={`${section}-${index}`}
            title={title}
            defaultExpanded={index === 0}
          >
            <MarkdownRenderer content={String(value)} metadata={metadata} />
          </CoreExpander>
        );
      })}
    </>
  );
};

export default ChatMessageClaimDetail;
